import { Box, Flex, Grid, Heading, Image, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { PillButton, IntroIcon } from '@denise-augusta/ui';
import { useSiteContent } from '@/providers/SiteContentProvider';
import { SiteFooter } from '@/layouts/PublicLayout';
import { RichText } from '@/components/RichText';

export function Home() {
  const content = useSiteContent();
  const marquee = [...content.marquee, ...content.marquee];

  return (
    <Box position="relative">
      {/* Fixed desk wallpaper behind every section */}
      <Box
        position="fixed"
        inset="0"
        bgImage="url('/images/desk-wallpaper.jpg')"
        bgSize="cover"
        bgPos="center"
        opacity="0.55"
        zIndex="-2"
        pointerEvents="none"
      />
      <Box
        position="fixed"
        inset="0"
        bgGradient="linear-gradient(180deg, rgba(245,244,241,0.32), rgba(234,237,245,0.27))"
        zIndex="-1"
        pointerEvents="none"
      />

      {/* HERO */}
      <Flex
        position="relative"
        w="100%"
        minH="82vh"
        overflow="hidden"
        align="center"
        bg="rgba(234,237,245,0.32)"
        direction={{ base: 'column', md: 'row' }}
      >
        <Box
          as="svg"
          position="absolute"
          top="0"
          left="0"
          w="100%"
          h={{ base: '200px', md: 'clamp(240px, 36vh, 380px)' }}
          zIndex="1"
          pointerEvents="none"
          filter="blur(3px)"
          // @ts-expect-error svg attrs pass through chakra
          viewBox="0 0 1600 500"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M 0,0 L 1600,0 L 1600,325 C 1340,342 1085,302 780,232 C 540,175 280,108 0,80 Z" fill="#1F2D5C" />
          <path d="M 0,0 L 1600,0 L 1600,278 C 1345,294 1115,256 850,198 C 600,142 310,75 0,55 Z" fill="#3D52A8" opacity="0.85" />
          <path d="M 0,55 C 310,75 600,142 850,198 C 1115,256 1345,294 1600,278" fill="none" stroke="#B5E061" strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <path d="M 0,0 L 1600,0 L 1600,235 C 1410,248 1185,213 920,162 C 640,110 320,55 0,35 Z" fill="#5C72BF" opacity="0.62" />
        </Box>

        <Box
          position={{ base: 'relative', md: 'absolute' }}
          left={{ md: '14%' }}
          top={{ md: '50%' }}
          transform={{ md: 'translateY(-44%)' }}
          mt={{ base: '8.5rem', md: 0 }}
          mx={{ base: 'auto', md: 0 }}
          w={{ base: '240px', md: 'clamp(340px, 38vw, 520px)' }}
          aspectRatio="4/5"
          zIndex="2"
          borderRadius="14px"
          overflow="hidden"
          boxShadow="0 30px 80px rgba(31,45,92,0.18)"
        >
          <Image
            src="/images/denise-photo.jpg"
            alt="Denise Augusta"
            w="100%"
            h="100%"
            objectFit="cover"
            objectPosition="center 20%"
          />
        </Box>

        <Box
          position="relative"
          zIndex="2"
          ml={{ md: 'auto' }}
          mr={{ md: '5vw' }}
          maxW={{ base: '100%', md: '560px' }}
          px={{ base: '1.8rem', md: 0 }}
          pl={{ md: '3.5rem' }}
          pr={{ md: '6.5rem' }}
          pt={{ base: '2.4rem', md: '16rem' }}
          pb={{ base: '5rem', md: '4rem' }}
          textAlign={{ base: 'center', md: 'left' }}
          css={{
            '& > *': { opacity: 0, animation: 'fadeUp 0.8s ease forwards' },
            '& > *:nth-of-type(1)': { animationDelay: '0.15s' },
            '& > *:nth-of-type(2)': { animationDelay: '0.28s' },
            '& > *:nth-of-type(3)': { animationDelay: '0.41s' },
            '& > *:nth-of-type(4)': { animationDelay: '0.54s' },
          }}
        >
          <Flex
            align="center"
            gap="0.85rem"
            justify={{ base: 'center', md: 'flex-start' }}
            fontSize="0.67rem"
            fontWeight="500"
            letterSpacing="0.28em"
            textTransform="uppercase"
            color="brand.mid"
            mb="1.8rem"
          >
            <Box display={{ base: 'none', md: 'block' }} w="28px" h="1px" bg="brand.mid" />
            {content.hero.eyebrow}
          </Flex>
          <Heading
            as="h1"
            fontFamily="heading"
            fontSize="clamp(3.8rem, 6.5vw, 7rem)"
            fontWeight="300"
            lineHeight="0.92"
            color="brand.ink"
            mb="1.25rem"
          >
            {content.hero.firstName}
            <br />
            <Box as="em" fontStyle="italic">
              {content.hero.lastName}
            </Box>
          </Heading>
          <Text
            fontSize="0.68rem"
            fontWeight="500"
            letterSpacing="0.22em"
            textTransform="uppercase"
            color="ink.muted"
            mb="2rem"
          >
            {content.hero.subtitle}
          </Text>
          <RichText
            html={content.hero.tagline}
            fontFamily="heading"
            fontStyle="italic"
            fontWeight="300"
            fontSize="clamp(1.05rem, 1.7vw, 1.28rem)"
            color="ink.body"
            lineHeight="1.72"
            maxW="460px"
            mx={{ base: 'auto', md: 0 }}
            px="1.5rem"
            py="1.2rem"
            borderLeft={{ base: 'none', md: '3px solid' }}
            borderTop={{ base: '3px solid', md: 'none' }}
            borderColor={{ base: 'brand.mid', md: 'brand.mid' }}
            bg="rgba(255,255,255,0.72)"
            backdropFilter="blur(8px)"
            borderRadius={{ base: '14px', md: '0 14px 14px 0' }}
          />
        </Box>
      </Flex>

      {/* MARQUEE */}
      <Box
        aria-hidden="true"
        bg="rgba(232,229,224,0.55)"
        borderY="1px solid"
        borderColor="paper.border"
        py="0.82rem"
        overflow="hidden"
        whiteSpace="nowrap"
        backdropFilter="blur(4px)"
      >
        <Box display="inline-flex" animation="ticker 30s linear infinite">
          {marquee.map((item, i) => (
            <Text
              key={i}
              as="span"
              fontSize="0.75rem"
              fontWeight="500"
              letterSpacing="0.17em"
              textTransform="uppercase"
              color="brand.ink"
              px="2.2rem"
            >
              {item} <Box as="span" color="ink.light" ml="2.2rem">·</Box>
            </Text>
          ))}
        </Box>
      </Box>

      {/* INTRO STRIP */}
      <Box bg="rgba(255,255,255,0.45)" px={{ base: '1.8rem', md: '3.5rem' }} py={{ base: '4rem', md: '3.5rem' }} backdropFilter="blur(6px)">
        <Grid
          maxW="1120px"
          mx="auto"
          templateColumns={{ base: '1fr', md: '1fr 1px 1fr 1px 1fr' }}
          gap={{ base: '2.5rem', md: '3rem' }}
          alignItems="start"
        >
          {content.intro.map((block, i) => (
            <Box key={block.title} display="contents">
              {i > 0 && (
                <Box display={{ base: 'none', md: 'block' }} bg="paper.border" alignSelf="stretch" opacity="0.5" />
              )}
              <Box>
                <Flex
                  w="46px"
                  h="46px"
                  bg="paper.warm"
                  border="1px solid"
                  borderColor="paper.border"
                  align="center"
                  justify="center"
                  mb="1.3rem"
                  borderRadius="12px"
                  color="brand.mid"
                >
                  <IntroIcon id={block.icon} />
                </Flex>
                <Heading as="h3" fontFamily="heading" fontSize="1.4rem" fontWeight="600" color="brand.ink" mb="0.6rem">
                  {block.title}
                </Heading>
                <RichText html={block.body} fontSize="0.87rem" color="ink.body" lineHeight="1.85" />
              </Box>
            </Box>
          ))}
        </Grid>
      </Box>

      {/* CTA + FOOTER share the bottom swoop */}
      <Box position="relative" overflow="hidden">
        <Box
          position="relative"
          bg="rgba(245,244,241,0.38)"
          borderTop="2.5px solid"
          borderTopColor="brand.lime"
          px={{ base: '1.8rem', md: '3.5rem' }}
          pt={{ base: '4rem', md: '3rem' }}
          pb={{ base: '6rem', md: '2rem' }}
          textAlign="center"
          backdropFilter="blur(4px)"
          zIndex="2"
        >
          <Heading
            as="h2"
            fontFamily="heading"
            fontSize="clamp(2.5rem, 5vw, 4rem)"
            fontWeight="300"
            color="brand.ink"
            mb="1.75rem"
          >
            Ready to get{' '}
            <Box as="em" fontStyle="italic" color="brand.mid">
              organized?
            </Box>
          </Heading>
          <Text fontFamily="heading" fontStyle="italic" fontSize="1.1rem" color="ink.body" mb="2.5rem">
            Let's talk about what you need — I respond within one business day.
          </Text>
          <PillButton asChild>
            <RouterLink to="/contact">Start the Conversation →</RouterLink>
          </PillButton>
        </Box>

        <SiteFooter copyright={content.footer.copyright} variant="glass" />

        <Box
          as="svg"
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          w="100%"
          h="clamp(100px, 13vh, 150px)"
          zIndex="1"
          pointerEvents="none"
          // @ts-expect-error svg attrs pass through chakra
          viewBox="0 0 1600 400"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M 0,400 L 1600,400 L 1600,0 C 1340,12 1080,55 780,140 C 530,200 280,232 0,245 Z" fill="#1F2D5C" />
          <path d="M 0,400 L 1600,400 L 1600,25 C 1345,38 1110,82 850,170 C 600,228 305,255 0,263 Z" fill="#3D52A8" opacity="0.85" />
          <path d="M 0,263 C 305,255 600,228 850,170 C 1110,82 1345,38 1600,25" fill="none" stroke="#B5E061" strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          <path d="M 1600,60 C 1415,75 1180,120 920,205 C 640,265 320,283 0,287 L 0,305 C 320,302 640,278 920,225 C 1180,142 1415,108 1600,95 Z" fill="#5C72BF" opacity="0.6" />
        </Box>
      </Box>
    </Box>
  );
}
